/**
 * Тревога «Рассылка дошла не всем» (план 163 Ф3, §2.3).
 *
 * ЗАЧЕМ. «Принято Личкой» записывается как `delivery_status = 'sent'`, а
 * провал приходит вебхуком статуса через 16+ минут. 17.10 из 562 картинок
 * через Личку не дошли 395 — и никто не узнал: журнал рассылок обновляется,
 * только пока вкладка открыта. Крон воркера (`broadcast-delivery-alert-scan`)
 * раз в 5 минут считает недоставленных людей по рассылкам за сутки и пишет
 * уведомление этого вида; колокольчик, пуш и Telegram берут тексты из общего
 * каталога (`employee-notification-kinds.ts`).
 *
 * Здесь — общие для воркера, API и админки правила: порог, повтор, группы
 * причин и роли с правом рассылки по умолчанию.
 */

export const MESSENGER_BROADCAST_UNDELIVERED = 'messenger.broadcast_undelivered';

/**
 * Порог (ответ владельца 18.10, §5 п. 2): не меньше 10 человек И не меньше
 * 10 % получателей. Единичные «чат не найден» бывают в каждой рассылке —
 * они тревогу не поднимают.
 */
export const BROADCAST_ALERT_MIN_PEOPLE = 10;
export const BROADCAST_ALERT_MIN_PERCENT = 10;

/**
 * Пора ли писать уведомление. `lastAlerted` — число недоставленных в прошлом
 * уведомлении по этой рассылке (null — уведомлений ещё не было). Повтор — при
 * ЛЮБОМ росте числа (ответ владельца 18.10); без роста — тишина.
 */
export function broadcastAlertDue(p: {
  failed: number;
  recipients: number;
  lastAlerted: number | null;
}): boolean {
  if (p.failed < BROADCAST_ALERT_MIN_PEOPLE) return false;
  // Целочисленно, без деления: failed / recipients ≥ 10 %.
  if (p.failed * 100 < p.recipients * BROADCAST_ALERT_MIN_PERCENT) return false;
  return p.lastAlerted === null || p.failed > p.lastAlerted;
}

// ---------------------------------------------------------------------------
// Причины недоставки — группами. В Telegram уходит только подпись группы и
// число: сырой текст ошибки платформы туда не попадает (в нём бывает chat_id).
// ---------------------------------------------------------------------------

export type BroadcastFailureGroup =
  | 'file_fetch'
  | 'file_rejected'
  | 'blocked'
  | 'chat_not_found'
  | 'other';

export const BROADCAST_FAILURE_LABELS: Readonly<Record<BroadcastFailureGroup, string>> = {
  file_fetch: 'Личка не скачала файл',
  file_rejected: 'файл не подошёл мессенджеру',
  blocked: 'бот заблокирован или чат закрыт',
  chat_not_found: 'чат не найден',
  other: 'другие причины',
};

/** Порядок групп при равных числах и в тексте. */
const GROUP_ORDER: readonly BroadcastFailureGroup[] = [
  'file_fetch',
  'file_rejected',
  'blocked',
  'chat_not_found',
  'other',
];

/**
 * Группа причины по тексту `messenger_messages.error`.
 *
 * Тексты скачивания файла пишет `wazzupStatusErrorText` (API): «Личка не
 * смогла скачать файл: …», код Лички в скобках. Остальное — описания платформ
 * как есть: Telegram «Forbidden: bot was blocked by the user», «Bad Request:
 * chat not found», MAX — `http_403 {"code":"chat.denied"…}`.
 */
export function broadcastFailureGroup(error: string | null | undefined): BroadcastFailureGroup {
  const e = (error ?? '').toLowerCase();
  if (!e) return 'other';
  if (e.includes('content_too_large')) return 'file_rejected';
  if (e.includes('личка не смогла скачать файл') || e.includes('content_unavailable')) {
    return 'file_fetch';
  }
  if (/blocked|forbidden|deactivated|kicked|dialog\.suspended|chat\.denied|заблокир/.test(e)) {
    return 'blocked';
  }
  if (/chat not found|chat_not_found|чат не найден|not\.found/.test(e)) return 'chat_not_found';
  return 'other';
}

export interface BroadcastFailureReason {
  group: BroadcastFailureGroup;
  count: number;
}

/** Сводка причин: строки «текст ошибки → людей» сводятся в группы. */
export function summarizeBroadcastFailures(
  rows: ReadonlyArray<{ error: string | null; people: number }>,
): BroadcastFailureReason[] {
  const counts = new Map<BroadcastFailureGroup, number>();
  for (const r of rows) {
    const g = broadcastFailureGroup(r.error);
    counts.set(g, (counts.get(g) ?? 0) + r.people);
  }
  return GROUP_ORDER.filter((g) => (counts.get(g) ?? 0) > 0)
    .map((g) => ({ group: g, count: counts.get(g)! }))
    .sort(
      (a, b) => b.count - a.count || GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group),
    );
}

/**
 * «Личка не скачала файл 391, другие причины 4» — из payload уведомления.
 * Неизвестная группа (писатель новее читателя) идёт как «другие причины».
 */
export function broadcastFailureReasonsText(reasons: unknown): string {
  if (!Array.isArray(reasons)) return '';
  const parts: string[] = [];
  for (const r of reasons) {
    if (!r || typeof r !== 'object') continue;
    const rec = r as Record<string, unknown>;
    const count = Number(rec['count']);
    if (!Number.isFinite(count) || count <= 0) continue;
    const label =
      BROADCAST_FAILURE_LABELS[rec['group'] as BroadcastFailureGroup] ??
      BROADCAST_FAILURE_LABELS.other;
    parts.push(`${label} ${count}`);
  }
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Кому, если автора нет (отложенная новость: «рассылку запустило время»).
// ---------------------------------------------------------------------------

/**
 * Роли, у которых право `messenger:broadcast` есть по умолчанию. Источник
 * истины — матрица `RbacService` в API; воркер её импортировать не может,
 * поэтому список повторён здесь, а тест API (`broadcast-alert-roles.spec.ts`)
 * сверяет его с матрицей.
 */
export const MESSENGER_BROADCAST_DEFAULT_ROLES: readonly string[] = ['boss', 'manager'];

/**
 * Активные сотрудники с правом `messenger:broadcast` (ответ владельца 18.10,
 * §5 п. 5) — по тем же правилам, что `RbacService.authorize`: право есть, если
 * его даёт основная ИЛИ вторая роль; правка из «Доступа»
 * (`role_permission_overrides`) поверх умолчания; владельца правки не
 * касаются. `$1` — тенант, `$2` — роли по умолчанию (text[]).
 */
export const BROADCAST_PERMISSION_HOLDERS_SQL = `
SELECT DISTINCT m.employee_id AS "id"
  FROM tenant_memberships m
  JOIN employees e ON e.tenant_id = m.tenant_id AND e.id = m.employee_id
 WHERE m.tenant_id = $1::uuid
   AND m.status = 'active'
   AND e.status = 'active' AND e.is_system = false
   AND EXISTS (
     SELECT 1
       FROM unnest(ARRAY[m.role::text, m.secondary_role::text]) AS r(role)
      WHERE r.role IS NOT NULL
        AND CASE
              WHEN r.role = 'boss' THEN true
              ELSE COALESCE(
                (SELECT o.allowed
                   FROM role_permission_overrides o
                  WHERE o.tenant_id = m.tenant_id
                    AND o.role = r.role
                    AND o.action = 'messenger:broadcast'),
                r.role = ANY($2::text[]))
            END)
`;
